import { createFileRoute } from '@tanstack/react-router';
import { Banner } from '@osd/ui/banner';
import { Card } from '@osd/ui/card';
import { Skeleton, SkeletonCircle, SkeletonText } from '@osd/ui/skeleton';
import { Stack } from '@osd/ui/stack';
import { Text } from '@osd/ui/text';
import { CodeBlock } from '@osd/ui/code-block';
import { Section } from '@osd/ui/section';

/**
 * /ui/skeleton — live docs for the `Skeleton` primitive.
 *
 * Skeleton is the content-shape placeholder used while data is
 * loading. Use it inside a region that's `aria-busy="true"` (the
 * `LoadingState` composite does this automatically) so screen
 * readers know the shape is loading without announcing every line.
 */
export const Route = createFileRoute('/ui/_layout/skeleton')({
  component: SkeletonPage,
});

function SkeletonPage() {
  return (
    <Stack gap={6}>
      <Section
        title="Skeleton"
        hint="Content-shape placeholder — single rect, multi-line text, or circle"
      >
        <Text>
          The wrapper renders <code>aria-hidden=&quot;true&quot;</code> by default — the loading
          region&rsquo;s parent should be <code>aria-busy=&quot;true&quot;</code>. Pass{' '}
          <code>ariaLabel</code> to promote a single skeleton to <code>role=status</code>.
        </Text>
      </Section>

      <Section title="Single rectangle" hint="Custom width / height / radius">
        <Stack gap={3}>
          <Skeleton width={240} height={24} />
          <Skeleton width="60%" height="2rem" radius="md" />
          <Skeleton width="100%" height="3rem" radius="lg" />
        </Stack>
      </Section>

      <Section title="Multi-line text" hint="SkeletonText — last row at 60%">
        <Stack gap={3}>
          <Card>
            <Card.Body>
              <SkeletonText lines={3} />
            </Card.Body>
          </Card>
          <SkeletonText lines={5} uniform />
        </Stack>
      </Section>

      <Section title="Round placeholders" hint="SkeletonCircle — avatars, icons">
        <Stack direction="row" gap={3} align="center">
          <SkeletonCircle size={32} />
          <SkeletonCircle size={48} />
          <SkeletonCircle size={64} />
        </Stack>
      </Section>

      <Section
        title="Realistic loading card"
        hint="Mimics a user-card layout — the skeleton shape matches the real content"
      >
        <Card>
          <Card.Body>
            <Stack direction="row" gap={3} align="center">
              <SkeletonCircle size={48} />
              <Stack gap={2} className="osd-flex-1">
                <Skeleton width="40%" height="1.25rem" />
                <SkeletonText lines={1} />
              </Stack>
            </Stack>
          </Card.Body>
        </Card>
      </Section>

      <Section title="Code example" hint="copy-pasteable usage">
        <Stack gap={5}>
          <CodeBlock
            language="tsx"
            title="Paragraph placeholder"
            code={`<SkeletonText lines={3} />`}
          />
          <CodeBlock
            language="tsx"
            title="Custom shape"
            code={`<Skeleton width={200} height={24} />
<Skeleton width="60%" height="2rem" radius="md" />`}
          />
          <CodeBlock
            language="tsx"
            title="With LoadingState (recommended)"
            code={`<LoadingState
  loading={users.isLoading}
  loadingFallback={
    <Stack gap={3}>
      <SkeletonCircle size={48} />
      <SkeletonText lines={3} />
    </Stack>
  }
>
  <UserList users={users.data} />
</LoadingState>`}
          />
        </Stack>
      </Section>

      <Banner variant="info" title="Accessibility">
        Under <code>prefers-reduced-motion: reduce</code> the pulse animation freezes to{' '}
        <code>0s</code> and the skeleton stays visible at a constant opacity of{' '}
        <code>var(--osd-skeleton-opacity)</code>. Honours WCAG 2.3.3.
      </Banner>
    </Stack>
  );
}
